import { computed, type ComputedRef } from 'vue'

import { useInsightActionSelection } from './useInsightActionSelection'

import { useInsightActionStore } from '../stores/insightActionStore'

import { useI18n } from '@/shared/i18n'
import type { UiTone } from '@/shared/ui/types'

export interface InsightActionBanner {
  key: string
  tone: UiTone
  text: string
}

export function useInsightActionBanners(): ComputedRef<InsightActionBanner[]> {
  const { t } = useI18n()
  const store = useInsightActionStore()
  const selection = useInsightActionSelection()

  return computed(() => {
    const group = selection.group.value
    if (!group) return []
    const out: InsightActionBanner[] = []

    if (group.approval?.approvalRequired) {
      const role = group.approval.requiredRole
      out.push({
        key: 'approval',
        tone: 'info',
        text: group.approval.requiresSecondPerson
          ? t('insightActions.select.banner.approvalSecondPerson', { role })
          : t('insightActions.select.banner.approval', { role }),
      })
    }

    if (group.summary.rowLimitTruncated) {
      out.push({
        key: 'truncated',
        tone: 'warning',
        text: t('insightActions.select.banner.truncated'),
      })
    }

    if (group.eligibilityState !== 'ready') {
      const pending = selection.listed.value.filter(
        (row) => row.eligibility?.state === 'pending',
      ).length
      out.push({
        key: 'readiness',
        tone: 'success',
        text: pending
          ? t('insightActions.select.banner.readinessPending', { count: pending })
          : t('insightActions.select.banner.readinessUnknown'),
      })
    }

    if (selection.allBlocked.value) {
      out.push({
        key: 'deferred',
        tone: 'warning',
        text: t('insightActions.select.banner.deferred'),
      })
    }

    if (store.executionEnabled === false) {
      out.push({ key: 'shadow', tone: 'info', text: t('insightActions.step.shadowNote') })
    }

    return out
  })
}
